/*
 * Knuska announcement bar - seamless marquee.
 *
 * Liquid ships one row of messages, centred and still. Two modes take it from
 * there, and either way that still row is the fallback.
 *
 * Rotate is the simple one: stack the messages, bring one up at a time on a
 * timer, pause while a pointer or the keyboard is on the bar.
 *
 * Marquee turns the row into a loop that never visibly repeats:
 *
 *   1. Reset the track to the one authored set.
 *   2. Repeat that set until it is at least as wide as the bar. A set narrower
 *      than the viewport would leave a visible empty stretch travelling across
 *      the bar once per lap.
 *   3. Clone the whole thing once. The track is now two identical halves, so
 *      translating -50% lands the second exactly where the first began - the
 *      seam has nothing to see.
 *   4. Set the duration from distance and speed, so px/second is honest at any
 *      width and the bar does not race on a phone and crawl on a desktop.
 *
 * Only then does .ka--running go on. Re-measured on resize, because step 2
 * depends on the width of the bar.
 */
if (!customElements.get('knuska-announcement')) {
  const REDUCED = '(prefers-reduced-motion: reduce)';
  const MAX_REPEATS = 24;

  class KnuskaAnnouncement extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ka-track]');
      this.source = this.querySelector('[data-ka-source]');
      if (!this.track || !this.source) return;

      /* The authored set, kept verbatim so every rebuild starts from it. */
      this.baseHTML = this.source.innerHTML;
      this.speed = Number(this.dataset.speed) || 45;

      if (this.dismissed()) {
        this.hidden = true;
        return;
      }

      this.bindClose();

      /*
       * Fonts change how wide the messages are, and the measurement has to
       * happen after they land or the track is built to the fallback's metrics.
       */
      if (this.dataset.mode === 'rotate') {
        this.rotate();
        return;
      }

      this.build();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.build());
      }

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.schedule());
        this.observer.observe(this);
      } else {
        this.onResize = () => this.schedule();
        window.addEventListener('resize', this.onResize);
      }
    }

    /* Rotate ------------------------------------------------------------- */

    /*
     * One message at a time. The stack is only laid out once .ka--running is
     * on, so the items are positioned and the first is marked active in the
     * same breath - otherwise there is a frame where every message is hidden.
     */
    rotate() {
      this.items = Array.prototype.slice.call(
        this.track.querySelectorAll('.ka__item')
      );
      if (!this.items.length) return;

      this.index = 0;
      this.classList.add('ka--running');
      this.items[0].classList.add('is-active');

      if (this.items.length < 2) return;

      const seconds = Number(this.dataset.interval) || 4;
      this.every = Math.max(seconds, 2) * 1000;

      this.start();
      this.bindArrows();

      if (this.classList.contains('ka--pausable')) {
        ['pointerenter', 'focusin'].forEach((type) =>
          this.addEventListener(type, () => this.stop())
        );
        ['pointerleave', 'focusout'].forEach((type) =>
          this.addEventListener(type, () => this.start())
        );
      }

      /*
       * A bar scrolled out of view still fires its timer, so a shopper coming
       * back to the top of the page meets whichever message the clock landed
       * on. Holding it still off-screen also saves the work.
       */
      if ('IntersectionObserver' in window) {
        this.seen = new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
        });
        this.seen.observe(this);
      }

      document.addEventListener('visibilitychange', this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.start();
      });
    }

    /*
     * Stepping by hand restarts the clock. Without that, a message moved to
     * deliberately can be taken away again a fraction of a second later,
     * because the interval was already most of the way through.
     */
    bindArrows() {
      const step = (dir) => {
        this.stop();
        this.go(dir);
        this.start();
      };

      const prev = this.querySelector('[data-ka-prev]');
      const next = this.querySelector('[data-ka-next]');
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
    }

    start() {
      if (this.timer || !this.every) return;
      this.timer = setInterval(() => this.go(1), this.every);
    }

    stop() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    go(dir) {
      const count = this.items.length;
      const leaving = this.items[this.index];
      /* + count keeps the modulo positive when stepping back from the first. */
      this.index = (this.index + dir + count) % count;
      const entering = this.items[this.index];

      this.classList.toggle('ka--back', dir < 0);

      leaving.classList.remove('is-active');
      leaving.classList.add('is-leaving');
      entering.classList.add('is-active');

      /*
       * The leaving class only exists to send that message upward rather than
       * down where the next one waits. Once it has gone it has to come off, or
       * every message ends up flagged as leaving and enters from the wrong
       * side on its next turn.
       */
      window.setTimeout(() => leaving.classList.remove('is-leaving'), 500);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.seen) this.seen.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onVisibility) {
        document.removeEventListener('visibilitychange', this.onVisibility);
      }
      if (this.frame) cancelAnimationFrame(this.frame);
      this.stop();
    }

    /* Resize fires in bursts; rebuild once when it settles. */
    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const width = this.clientWidth;
        if (width === this.builtFor) return;
        this.build();
      });
    }

    build() {
      const width = this.clientWidth;
      if (!width) return;

      this.classList.remove('ka--running');
      this.classList.add('ka--measuring');

      this.track.innerHTML = this.baseHTML;

      /* Repeat the authored set until one lap is at least a bar wide. */
      let repeats = 1;
      while (this.track.scrollWidth < width && repeats < MAX_REPEATS) {
        this.track.insertAdjacentHTML('beforeend', this.baseHTML);
        repeats += 1;
      }

      const lap = this.track.scrollWidth;
      if (!lap) {
        this.classList.remove('ka--measuring');
        return;
      }

      /* The second half. Hidden from assistive tech - it is the same words. */
      const half = this.track.innerHTML;
      this.track.insertAdjacentHTML('beforeend', half);

      const items = Array.prototype.slice.call(this.track.children);
      items.slice(items.length / 2).forEach((item) => {
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('data-ka-clone', '');
        /* A cloned link must not be a second tab stop for the same target. */
        const link = item.querySelector('a');
        if (link) link.setAttribute('tabindex', '-1');
      });

      this.style.setProperty('--ka-duration', lap / this.speed + 's');
      this.builtFor = width;

      this.classList.remove('ka--measuring');
      if (!window.matchMedia || !window.matchMedia(REDUCED).matches) {
        this.classList.add('ka--running');
      }
    }

    /* Dismissal ---------------------------------------------------------- */

    /*
     * Keyed to the messages themselves, so closing the bar hides these words
     * rather than the bar forever: change the copy and it comes back, which is
     * the behaviour a merchant expects when they write a new announcement.
     */
    storageKey() {
      const id = this.dataset.dismissible;
      if (!id) return null;
      let hash = 0;
      const text = this.track.textContent.replace(/\s+/g, ' ').trim();
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash << 5) - hash + text.charCodeAt(i);
        hash |= 0;
      }
      return 'knuska:announcement:' + id + ':' + hash;
    }

    dismissed() {
      const key = this.storageKey();
      if (!key) return false;
      try {
        return window.localStorage.getItem(key) === '1';
      } catch (e) {
        /* Private mode, or site data blocked. Show the bar. */
        return false;
      }
    }

    bindClose() {
      const button = this.querySelector('[data-ka-close]');
      if (!button) return;

      button.addEventListener('click', () => {
        const key = this.storageKey();
        if (key) {
          try {
            window.localStorage.setItem(key, '1');
          } catch (e) {
            /* Not being able to remember is not a reason not to close. */
          }
        }
        this.hidden = true;
      });
    }
  }

  customElements.define('knuska-announcement', KnuskaAnnouncement);
}
